import { Injectable } from "@angular/core";
import { MICustomFunctionsParams } from "../../../../models/fields/custom-function-params";
import { MCCodeListWrapperService, MCConfigService, MCCustomItemFourDigitsService, MCFormisFieldAutoCompleteService, MCPersonalImportService, MCRegex } from "@maslul/common-services";
import { RequestApprovalTypeDetailsStore } from "../../../store/approval-type-details-store";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { QuantityUnitCode } from "../quantiny-unitcode";

@Injectable({
    providedIn: 'root'
})
export class MICustomBookService {

    initCustomItemList(customFunctionsObj: MICustomFunctionsParams) {
        const value = customFunctionsObj?.value;
        const injector = customFunctionsObj.injector;
        const personalImportService = injector.get(MCPersonalImportService);
        const formisFieldAutoCompleteService = injector.get(MCFormisFieldAutoCompleteService);
        const requestApprovalTypeDetailsStore = injector.get(RequestApprovalTypeDetailsStore);
        const customItemFourDigitsService: MCCustomItemFourDigitsService = injector.get(MCCustomItemFourDigitsService);

        const is4DigitsCustomItem = requestApprovalTypeDetailsStore.getSnapshot().is4DigitsCustomItem
            && customFunctionsObj.value?.length == 4;

        const customItemField = this.getCustomItemField(customFunctionsObj);
        const fieldName = (customItemField?.key || 'customItem').toString();
        formisFieldAutoCompleteService.noMessage.next({ condition: false, fieldName: fieldName });

        if (MCRegex.digitsWithNegative.test(value) && value?.length >= 4) {
            formisFieldAutoCompleteService.loadingList.next({ condition: true, fieldName: fieldName });

            personalImportService.getCustomItemData(value).subscribe((res) => {
                formisFieldAutoCompleteService.noMessage.next({ condition: false, fieldName: fieldName });
                formisFieldAutoCompleteService.loadingList.next({ condition: false, fieldName: fieldName });

                const options = is4DigitsCustomItem ? customItemFourDigitsService.groupCustomItemBy4Digits(res) : res;
                if (customItemField?.props?.options) {
                    customItemField.props.options = options?.map((item: any) => ({ value: item.itemNum, label: item.itemNum })) || [];
                    formisFieldAutoCompleteService.externalRefreshList.next(true);
                }
            });
        } else {
            formisFieldAutoCompleteService.noMessage.next({ condition: true, fieldName: fieldName });
            formisFieldAutoCompleteService.externalRefreshList.next(true);
        }
    }

    onCustomItemSelected(customFunctionsObj: MICustomFunctionsParams) {
        if (!this.isDynamicApprovalType(customFunctionsObj)) return;

        const itemNum = this.extractItemNum(customFunctionsObj);
        if (!itemNum) return;

        const declaredUnitCode = this.getDeclaredUnitCodeField(customFunctionsObj);
        if (!declaredUnitCode) return;

        const pattern = this.extractPattern(itemNum);
        this.fetchAndSetMeasurementUnit(customFunctionsObj, pattern, itemNum, declaredUnitCode);
    }

    // ========== Private Methods ==========

    private isDynamicApprovalType(customFunctionsObj: MICustomFunctionsParams): boolean {
        const injector = customFunctionsObj.injector;
        const configService = injector.get(MCConfigService);
        const approvalTypeStore = injector.get(RequestApprovalTypeDetailsStore);

        const approvalTypeId = parseInt(approvalTypeStore.getSnapshot().approvalTypeId);
        const dynamicTypes: number[] = configService.getConfiguration().dynamicQuantityApprovalTypes || [];

        return dynamicTypes.includes(approvalTypeId);
    }

    private extractItemNum(customFunctionsObj: MICustomFunctionsParams): string | null {
        const selectedValue = customFunctionsObj.value;
        if (!selectedValue) return null;

        return selectedValue?.value?.value || selectedValue?.value || selectedValue;
    }

    private extractPattern(itemNum: string): string {
        return typeof itemNum === 'string' ? itemNum.split('/')[0] : itemNum;
    }

    private getDeclaredUnitCodeField(customFunctionsObj: MICustomFunctionsParams): FormlyFieldConfig | undefined {
        const quantities = customFunctionsObj.formisForm.fields
            ?.find((x: FormlyFieldConfig) => x.key == 'quantities');

        return quantities?.fieldGroup?.find(
            (x: FormlyFieldConfig) => x.key == "declaredUnitCode"
        );
    }

    private getCustomItemField(customFunctionsObj: MICustomFunctionsParams): FormlyFieldConfig | undefined {
        const customItemFieldSec = customFunctionsObj?.formisForm?.fields
            ?.find(x => x.key == 'customItem');

        return customItemFieldSec?.fieldGroup?.find(x => x.key == 'transportCustomItem')
            || customItemFieldSec?.fieldGroup?.find(x => x.key == 'customItem');
    }

    private fetchAndSetMeasurementUnit(
        customFunctionsObj: MICustomFunctionsParams,
        pattern: string,
        itemNum: string,
        declaredUnitCode: FormlyFieldConfig
    ) {
        const injector = customFunctionsObj.injector;
        const personalImportService = injector.get(MCPersonalImportService);
        const codeListService = injector.get(MCCodeListWrapperService);
        const quantityUnitCode = injector.get(QuantityUnitCode);

        personalImportService.getCustomItemData(pattern).subscribe((res) => {
            if (!res || res.length === 0) return;

            const selectedItem = res.find(item => item.itemNum === itemNum) || res[0];
            if (!selectedItem.measurementUnitID) return;

            this.setUnitCodeFromCodelist(codeListService, selectedItem.measurementUnitID, declaredUnitCode, quantityUnitCode, customFunctionsObj);
        });
    }

    private setUnitCodeFromCodelist(
        codeListService: MCCodeListWrapperService,
        measurementUnitID: number,
        declaredUnitCode: FormlyFieldConfig,
        quantityUnitCode: QuantityUnitCode,
        customFunctionsObj: MICustomFunctionsParams
    ) {
        codeListService.getListAllColums('UR_RQLMeaserType').subscribe(items => {
            const matchedUnit = items.find(i => i.value == measurementUnitID);
            if (matchedUnit) {
                declaredUnitCode.formControl?.setValue(matchedUnit);
                quantityUnitCode.updateQuantityFieldLabel(customFunctionsObj, matchedUnit);
            }
        });
    }
}
